import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { AlertTriangle, ArrowLeft, BookOpen, FileDown, Gauge, ImageDown, Loader2, Sparkles, Unlink, Users } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { dammInfo, LAYERS } from "../lib/constants";
import FlowMap from "../components/FlowMap";
import EntityPortal from "../components/EntityPortal";
import StoryPanel, { StoryPlayer } from "../components/StoryMode";
import Rollmatris from "../components/Rollmatris";

const ALL_LAYERS = { relationellt: true, strukturellt: true, systemiskt: true, digitalt: true, samhalleligt: true };

const AUDIENCES = [
    { id: "ledning", label: "Ledning" },
    { id: "chef", label: "Chef" },
    { id: "medarbetare", label: "Medarbetare" },
    { id: "roll", label: "Enskild roll" },
];

const SECTIONS = [
    { id: "kpis", label: "KPI:er" },
    { id: "karta", label: "Helhetskarta" },
    { id: "dammar", label: "Dammar" },
    { id: "rapport", label: "Rapporttext" },
    { id: "roller", label: "Rollmatris" },
];

function ReportBlock({ pres, label, testid }) {
    return (
        <div data-testid={testid} className="ed-rise mb-5 rounded-xl border border-current/30 bg-current/5 p-4" style={{ animationDelay: "120ms" }}>
            <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-1.5">{label} (AI-genererad)</p>
            <h3 className="font-display font-bold text-lg text-slate-100">{pres.titel}</h3>
            {pres.huvudbudskap && <p className="text-sm text-slate-200 mt-1 leading-relaxed">{pres.huvudbudskap}</p>}
            <div className="mt-3 space-y-3">
                {(pres.sektioner || []).map((s, i) => (
                    <div key={i}>
                        <p className="text-sm font-semibold text-slate-200">{s.rubrik}</p>
                        {s.text && <p className="text-xs text-slate-400 mt-0.5 leading-relaxed">{s.text}</p>}
                        {(s.punkter || []).map((p, j) => <p key={j} className="text-xs text-slate-300 mt-0.5">· {p}</p>)}
                    </div>
                ))}
            </div>
            {pres.avslut && (
                <div className="mt-3">
                    <p className="font-mono text-[9px] uppercase tracking-widest text-buoy mb-1">Rekommendationer & nästa steg</p>
                    <p className="text-xs text-slate-200 leading-relaxed">{pres.avslut}</p>
                </div>
            )}
        </div>
    );
}

export default function Presentation() {
    const { projectId } = useParams();
    const [graph, setGraph] = useState(null);
    const [exporting, setExporting] = useState(null);
    const [generating, setGenerating] = useState(false);
    const [generatingAud, setGeneratingAud] = useState(false);
    const [layers, setLayers] = useState(ALL_LAYERS);
    const [audience, setAudience] = useState("ledning");
    const [roleId, setRoleId] = useState("");
    const [roleRows, setRoleRows] = useState([]);
    const [roleRowsError, setRoleRowsError] = useState(false);
    const [sections, setSections] = useState({ kpis: true, karta: true, dammar: true, rapport: true, roller: false });
    const [portalId, setPortalId] = useState(null);
    const [storyOpen, setStoryOpen] = useState(false);
    const [storyPrefill, setStoryPrefill] = useState(null);
    const [playing, setPlaying] = useState(null);
    const requestedAud = useRef(new Set());

    useEffect(() => {
        if (sections.roller) {
            setTimeout(() => document.querySelector('[data-testid="ed-rollmatris"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
        }
    }, [sections.roller]);

    useEffect(() => {
        setRoleId("");
        setRoleRows([]);
        setRoleRowsError(false);
        setPortalId(null);
        api.get(`/projects/${projectId}/graph`).then((r) => setGraph(r.data)).catch(() => toast.error("Kunde inte ladda projektet"));
        api.get(`/projects/${projectId}/rollmatris`)
            .then((r) => setRoleRows(r.data.rows || []))
            .catch((e) => { setRoleRowsError(true); toast.error(formatApiError(e, "Kunde inte ladda rollerna")); });
    }, [projectId]);

    const pollForNewDraft = (known, typ, onDone, onFail, mottagare = null) => {
        const started = Date.now();
        const poll = async () => {
            try {
                const r = await api.get(`/projects/${projectId}/graph`);
                const fresh = r.data.drafts.filter((d) => d.typ === typ && !known.has(d.id) && (!mottagare || d.mottagare === mottagare));
                if (fresh.some((d) => d.status === "fel")) { onFail(); return; }
                if (fresh.some((d) => d.status === "godkand")) { onDone(r.data); return; }
            } catch { /* fortsätt polla */ }
            if (Date.now() - started > 150000) { onFail(); return; }
            setTimeout(poll, 5000);
        };
        setTimeout(poll, 5000);
    };

    const requestAudience = async (aud) => {
        if (!graph) return;
        setGeneratingAud(true);
        const known = new Set(graph.drafts.map((d) => d.id));
        try {
            await api.post(`/projects/${projectId}/audience-report`, { mottagare: aud });
            toast.info(`Anpassar presentationen för ${AUDIENCES.find((a) => a.id === aud)?.label.toLowerCase()} …`);
        } catch (e) {
            toast.error(formatApiError(e, "Kunde inte starta anpassningen"));
            requestedAud.current.delete(aud);
            setGeneratingAud(false);
            return;
        }
        pollForNewDraft(known, "mottagarrapport",
            (data) => { setGraph(data); toast.success("Presentationen är anpassad"); setGeneratingAud(false); },
            () => { toast.error("Anpassningen misslyckades – försök igen"); requestedAud.current.delete(aud); setGeneratingAud(false); },
            aud);
    };

    const audDraft = graph && (audience === "chef" || audience === "medarbetare")
        ? graph.drafts.find((d) => d.typ === "mottagarrapport" && d.status === "godkand" && d.mottagare === audience)
        : null;

    useEffect(() => {
        if (!graph || audience === "ledning" || audience === "roll") return;
        if (audDraft || generatingAud || requestedAud.current.has(audience)) return;
        requestedAud.current.add(audience);
        requestAudience(audience);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [audience, graph]);

    const capture = async () => {
        const el = document.getElementById("presentation-export");
        return html2canvas(el, { backgroundColor: "#060B11", scale: 2, useCORS: true });
    };

    const exportPNG = async () => {
        setExporting("png");
        try {
            const canvas = await capture();
            const a = document.createElement("a");
            a.download = `${graph.project.name}-executive-dashboard.png`;
            a.href = canvas.toDataURL("image/png");
            a.click();
        } catch {
            toast.error("PNG-exporten misslyckades");
        } finally {
            setExporting(null);
        }
    };

    const exportPDF = async () => {
        setExporting("pdf");
        try {
            const canvas = await capture();
            const pdf = new jsPDF({ orientation: "landscape", unit: "px", format: [canvas.width, canvas.height], hotfixes: ["px_scaling"] });
            pdf.addImage(canvas.toDataURL("image/png"), "PNG", 0, 0, canvas.width, canvas.height);
            pdf.save(`${graph.project.name}-ledningsrapport.pdf`);
        } catch {
            toast.error("PDF-exporten misslyckades");
        } finally {
            setExporting(null);
        }
    };

    const generateReport = async () => {
        if (generating || !graph) return;
        setGenerating(true);
        const known = new Set(graph.drafts.map((d) => d.id));
        try {
            await api.post(`/projects/${projectId}/leadership-report`);
            toast.info("Ledningsrapporten genereras – vyn fylls automatiskt när den är klar …");
        } catch (e) {
            toast.error(formatApiError(e, "Kunde inte starta genereringen"));
            setGenerating(false);
            return;
        }
        pollForNewDraft(known, "presentation",
            (data) => { setGraph(data); toast.success("Ledningsrapporten är klar – vyn är uppdaterad"); setGenerating(false); },
            () => { toast.error("Genereringen misslyckades – försök igen"); setGenerating(false); });
    };

    if (!graph) {
        return (
            <div className="h-screen bg-abyss flex items-center justify-center">
                <Loader2 className="w-5 h-5 animate-spin text-current" />
            </div>
        );
    }

    const confirmed = graph.bottlenecks.filter((b) => b.status === "bekraftad");
    const nodeLabel = (nid) => graph.nodes.find((n) => n.id === nid)?.label || "?";
    const targetLabel = (b) => {
        if (b.target_type === "nod") return nodeLabel(b.target_id);
        const fl = graph.flows.find((f) => f.id === b.target_id);
        return fl ? `${nodeLabel(fl.source)} → ${nodeLabel(fl.target)}` : "–";
    };
    const summary = graph.drafts.find((d) => d.typ === "sammanfattning" && d.status === "godkand");
    let summaryText = null;
    if (summary) {
        try { summaryText = JSON.parse(summary.innehall).text; } catch { summaryText = null; }
    }
    const presDraft = graph.drafts.find((d) => d.typ === "presentation" && d.status === "godkand");
    let pres = null;
    if (presDraft) {
        try { pres = JSON.parse(presDraft.innehall); } catch { pres = null; }
    }
    let audPres = null;
    if (audDraft) {
        try { audPres = JSON.parse(audDraft.innehall); } catch { audPres = null; }
    }

    const kritiska = confirmed.filter((b) => b.allvarlighet >= 4).length;
    const effektivitet = graph.flows.length
        ? Math.round((100 * graph.flows.filter((f) => (f.flodesstatus || "nar_fram") === "nar_fram").length) / graph.flows.length)
        : null;
    const infobrott = graph.flows.filter((f) => f.flodesstatus && f.flodesstatus !== "nar_fram").length;
    const anstallda = graph.nodes.filter((n) => n.type === "avdelning").reduce((s, n) => s + (n.antal_anstallda || 0), 0);
    const kpis = [
        { id: "kritiska", label: "Kritiska risker", value: kritiska, sub: "bekräftade dammar, allvarlighet 4–5", icon: AlertTriangle, color: "#EF4444" },
        { id: "effektivitet", label: "Effektivitetsindex", value: effektivitet === null ? "–" : `${effektivitet} %`, sub: "flöden där information når fram", icon: Gauge, color: "#34D399" },
        { id: "infobrott", label: "Informationsbrott", value: infobrott, sub: "flöden som förändras eller stannar", icon: Unlink, color: "#F59E0B" },
        { id: "anstallda", label: "Kartlagd organisation", value: anstallda, sub: "anställda i kartlagda avdelningar", icon: Users, color: "#38BDF8" },
    ];
    const audLabel = AUDIENCES.find((a) => a.id === audience)?.label;

    return (
        <div className="h-screen bg-abyss flex flex-col" data-testid="presentation-mode">
            <header className="h-16 shrink-0 flex items-center gap-3 px-4 xl:px-6 border-b border-border bg-canvas/80 backdrop-blur-md">
                <Link data-testid="exit-presentation-button" to="/" className="flex items-center gap-2 text-slate-400 hover:text-current transition-colors">
                    <ArrowLeft className="w-4 h-4" />
                    <span className="text-xs font-medium">Avsluta</span>
                </Link>
                <div className="hidden sm:flex items-center gap-2">
                    <img src="/hamn-emblem.png" alt="HAMN" className="w-6 h-6 rounded-full object-cover" />
                    <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">HAMN ATLAS · Executive Dashboard</span>
                </div>

                <div className="hidden lg:flex items-center gap-1 bg-abyss/60 border border-border rounded-lg p-1 ml-1" data-testid="ed-audience-switcher">
                    {AUDIENCES.map((a) => (
                        <button key={a.id} data-testid={`ed-audience-${a.id}`} onClick={() => setAudience(a.id)}
                            className={`rounded-md px-2 py-1 text-[10px] font-medium transition-colors ${audience === a.id ? "bg-current/20 text-current" : "text-slate-500 hover:text-slate-300"}`}>
                            {a.label}
                        </button>
                    ))}
                </div>

                <div className="hidden xl:flex items-center gap-1 bg-abyss/60 border border-border rounded-lg p-1" data-testid="ed-layer-switcher">
                    {LAYERS.map((l) => (
                        <button key={l.id} data-testid={`ed-layer-${l.id}`}
                            onClick={() => setLayers((prev) => ({ ...prev, [l.id]: !prev[l.id] }))}
                            className={`flex items-center gap-1.5 rounded-md px-2 py-1 text-[10px] font-medium transition-colors duration-300 ${layers[l.id] ? "text-slate-100 bg-deepsea" : "text-slate-600 hover:text-slate-400"}`}>
                            <span className="w-1.5 h-1.5 rounded-full transition-transform duration-300"
                                style={{ background: layers[l.id] ? l.color : "#334155", transform: layers[l.id] ? "scale(1.5)" : "scale(1)" }} />
                            {l.label}
                        </button>
                    ))}
                </div>

                <div className="flex-1" />
                <button data-testid="story-open-button" onClick={() => setStoryOpen(true)}
                    className="flex items-center gap-2 text-xs font-semibold border border-border text-slate-300 rounded-lg px-2.5 py-2 hover:border-current/50 hover:text-current transition-colors">
                    <BookOpen className="w-4 h-4" /> <span className="hidden lg:inline">Berättelse</span>
                </button>
                <button data-testid="generate-leadership-report" onClick={generateReport} disabled={generating}
                    className="flex items-center gap-2 text-xs font-semibold bg-aidraft/15 text-aidraft border border-aidraft/40 rounded-lg px-2.5 xl:px-3 py-2 hover:bg-aidraft/25 transition-colors disabled:opacity-50">
                    {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    <span className="hidden lg:inline">Generera ledningsrapport</span>
                </button>
                <button data-testid="export-png-button" onClick={exportPNG} disabled={!!exporting}
                    className="flex items-center gap-2 text-xs font-semibold border border-border text-slate-300 rounded-lg px-2.5 xl:px-3 py-2 hover:border-current/50 hover:text-current transition-colors disabled:opacity-50">
                    {exporting === "png" ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageDown className="w-4 h-4" />} PNG
                </button>
                <button data-testid="export-pdf-button" onClick={exportPDF} disabled={!!exporting}
                    className="flex items-center gap-2 text-xs font-semibold bg-current text-abyss rounded-lg px-2.5 xl:px-3 py-2 hover:bg-tide transition-colors disabled:opacity-50">
                    {exporting === "pdf" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />} <span className="hidden lg:inline">Exportera PDF</span>
                </button>
            </header>

            <div className="shrink-0 flex items-center gap-2 px-4 xl:px-6 py-2 border-b border-border bg-canvas/40 flex-wrap" data-testid="ed-section-toolbar">
                <span className="font-mono text-[9px] uppercase tracking-widest text-slate-500 mr-1">Visa:</span>
                {SECTIONS.map((s) => (
                    <button key={s.id} data-testid={`ed-section-${s.id}`}
                        onClick={() => setSections((p) => ({ ...p, [s.id]: !p[s.id] }))}
                        className={`text-[10px] rounded-full border px-2.5 py-1 transition-colors ${sections[s.id] ? "border-current/50 text-current bg-current/10" : "border-border text-slate-500"}`}>
                        {s.label}
                    </button>
                ))}
                {audience === "roll" && (
                    <select data-testid="ed-role-select" value={roleId}
                        onChange={(e) => {
                            setRoleId(e.target.value);
                            const row = roleRows.find((r) => r.roll_id === e.target.value);
                            if (row) setPortalId(row.hemma_id);
                        }}
                        className="bg-abyss/60 border border-border rounded-lg px-2 py-1 text-[10px] text-slate-200 outline-none focus:border-current/60">
                        <option value="">Välj roll att presentera …</option>
                        {roleRows.map((r) => <option key={r.roll_id} value={r.roll_id}>{r.namn} · {r.hemma_label}</option>)}
                    </select>
                )}
                {audience === "roll" && roleRowsError && (
                    <div className="flex items-center gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-2.5 py-1 text-[10px] text-red-300" data-testid="ed-roles-error">
                        Rollerna kunde inte laddas
                        <button data-testid="ed-roles-retry" onClick={() => {
                            setRoleRowsError(false);
                            api.get(`/projects/${projectId}/rollmatris`)
                                .then((r) => setRoleRows(r.data.rows || []))
                                .catch((e) => { setRoleRowsError(true); toast.error(formatApiError(e, "Kunde inte ladda rollerna")); });
                        }} className="underline hover:text-red-200">Försök igen</button>
                    </div>
                )}
                <span className="font-mono text-[9px] uppercase tracking-widest text-slate-600 ml-auto hidden md:inline">
                    Mottagare: {audLabel}
                </span>
            </div>

            <div id="presentation-export" className="flex-1 flex flex-col min-h-0 bg-abyss">
                {sections.kpis && (
                    <div className="shrink-0 grid grid-cols-2 xl:grid-cols-4 gap-3 xl:gap-4 px-4 xl:px-6 pt-4 xl:pt-5" data-testid="ed-kpi-strip">
                        {kpis.map((k, i) => (
                            <div key={k.id} data-testid={`ed-kpi-${k.id}`}
                                className="ed-rise relative overflow-hidden rounded-2xl border border-border bg-deepsea/60 p-4 xl:p-5"
                                style={{ animationDelay: `${i * 90}ms` }}>
                                <div className="absolute -right-5 -top-5 w-20 h-20 rounded-full blur-2xl" style={{ background: `${k.color}1e` }} />
                                <k.icon className="w-4 h-4" style={{ color: k.color }} />
                                <p className="font-mono text-[9px] uppercase tracking-widest text-slate-500 mt-2.5">{k.label}</p>
                                <p className="font-display font-extrabold text-3xl xl:text-4xl tracking-tight mt-1" style={{ color: k.color }}>{k.value}</p>
                                <p className="text-[10px] text-slate-500 mt-1">{k.sub}</p>
                            </div>
                        ))}
                    </div>
                )}

                <div className="flex-1 flex min-h-0 pt-3 xl:pt-4">
                    {sections.karta && (
                        <div className="flex-1 relative min-w-0">
                            <div className="absolute top-4 left-6 z-10 pointer-events-none">
                                <h1 className="font-display font-extrabold text-3xl tracking-tight text-slate-100">{graph.project.name}</h1>
                                <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mt-1">
                                    Flödesbild · {confirmed.length} bekräftade dammar · {graph.project.demo ? "syntetisk demodata" : graph.project.organisation}
                                </p>
                                <p className="font-mono text-[9px] uppercase tracking-widest text-current/70 mt-1">Klicka på en nod för kontext-portal</p>
                            </div>
                            <FlowMap graph={graph} layers={layers} selection={null} presentation
                                onSelect={(sel) => { if (sel.kind === "node") setPortalId(sel.id); }} />
                        </div>
                    )}

                    <aside className={`${sections.karta ? "w-[300px] xl:w-[400px] shrink-0 border-l" : "flex-1"} border-border p-4 xl:p-6 overflow-y-auto panel-scroll`}>
                        {sections.dammar && (
                            <>
                                <h2 className="font-display font-bold text-xl text-slate-100 mb-1">Var uppstår dammarna – och varför</h2>
                                <p className="text-xs text-slate-500 mb-5">Allt nedan är förankrat i insamlad empiri.</p>
                            </>
                        )}
                        {sections.rapport && audience === "ledning" && pres && (
                            <ReportBlock pres={pres} label="Ledningsrapport" testid="presentation-hub-draft" />
                        )}
                        {sections.rapport && (audience === "chef" || audience === "medarbetare") && (
                            audPres ? (
                                <ReportBlock pres={audPres} label={`Anpassad för ${audLabel.toLowerCase()}`} testid="audience-report" />
                            ) : (
                                <div data-testid="audience-report-loading" className="mb-5 rounded-xl border border-border bg-deepsea/40 p-4 flex items-center gap-2 text-xs text-slate-500">
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Anpassar presentationen för {audLabel.toLowerCase()} …
                                </div>
                            )
                        )}
                        {sections.rapport && audience === "roll" && (
                            <div className="mb-5 rounded-xl border border-border bg-deepsea/40 p-4 text-xs text-slate-500" data-testid="audience-roll-hint">
                                Välj en roll i listan ovan – dess kontext-portal öppnas med rollbeskrivning, flöden, kostnad och AI-förklaring anpassad för mottagaren.
                            </div>
                        )}
                        {sections.dammar && (
                            <div className="space-y-3">
                                {confirmed.length === 0 && <p className="text-sm text-slate-500">Inga bekräftade dammar ännu.</p>}
                                {confirmed.sort((a, b) => b.allvarlighet - a.allvarlighet).map((b, i) => {
                                    const info = dammInfo(b);
                                    return (
                                        <div key={b.id} data-testid={`presentation-dam-${b.id}`} className="ed-rise rounded-xl border p-4"
                                            style={{ borderColor: `${info.color}44`, background: `${info.color}08`, animationDelay: `${200 + i * 70}ms` }}>
                                            <div className="flex items-center justify-between mb-1">
                                                <span className="text-sm font-bold" style={{ color: info.color }}>{info.label}</span>
                                                <div className="flex gap-1">
                                                    {[1, 2, 3, 4, 5].map((v) => (
                                                        <span key={v} className="w-4 h-1.5 rounded-full" style={{ background: v <= b.allvarlighet ? info.color : "#1E293B" }} />
                                                    ))}
                                                </div>
                                            </div>
                                            <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-1.5">{targetLabel(b)}</p>
                                            <p className="text-sm text-slate-300 leading-relaxed">{b.notering}</p>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        {sections.roller && (
                            <div className="mt-6" data-testid="ed-rollmatris">
                                <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">Rollmatris – rapporteringslinjer</p>
                                <Rollmatris projectId={projectId} compact rowTestIdPrefix="ed-rollmatris-row"
                                    onOpenNode={(nodeId, rollId) => { setAudience("roll"); setRoleId(rollId); setPortalId(nodeId); }} />
                            </div>
                        )}
                        {summaryText && sections.rapport && (
                            <div className="mt-6 rounded-xl border border-border bg-deepsea/50 p-4">
                                <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">Sammanfattning (granskad)</p>
                                <p className="text-sm text-slate-300 leading-relaxed">{summaryText}</p>
                            </div>
                        )}
                    </aside>
                </div>
            </div>

            {portalId && (
                <div className="fixed inset-y-0 right-0 w-full sm:w-[440px] xl:w-[520px] z-40 border-l border-border bg-canvas shadow-2xl flex flex-col"
                    data-testid="entity-portal">
                    <EntityPortal nodeId={portalId} audience={audience} focusRoleId={audience === "roll" ? roleId : null} onClose={() => setPortalId(null)}
                        onAddToStory={(step) => { setStoryPrefill(step); setStoryOpen(true); }} />
                </div>
            )}

            {storyOpen && !playing && (
                <StoryPanel projectId={projectId} graph={graph} prefill={storyPrefill}
                    onPrefillConsumed={() => setStoryPrefill(null)}
                    onPlay={(s) => { setPlaying(s); setStoryOpen(false); }}
                    onClose={() => setStoryOpen(false)} />
            )}
            {playing && (
                <StoryPlayer story={playing} graph={graph} layers={layers} onExit={() => setPlaying(null)} />
            )}
        </div>
    );
}
